import type { CredentialProvider, SDKCredential } from "@signalwire/js";

export interface SubscriberTokenResponse {
  token: string;
  refreshToken: string | null;
}

// Subscriber access tokens live for 2 hours. We report each one as expiring
// after 50 minutes so the SDK's own refresh timer (which fires just before
// `expiry_at`) renews it with plenty of margin, including after a laptop has
// slept through part of that window.
export const TOKEN_REFRESH_AFTER_MS = 50 * 60 * 1000;

// Refresh tokens expire after 2h 5min. Use a 10-minute buffer to avoid
// sending an expired token (e.g. after laptop sleep).
export const REFRESH_TOKEN_MAX_AGE_MS = (2 * 60 + 5 - 10) * 60 * 1000; // 115 minutes

// How long the token we already hold can still be handed back when a refresh
// fails: its 2h life less a 5-minute margin.
export const TOKEN_USABLE_FOR_MS = (2 * 60 - 5) * 60 * 1000; // 115 minutes

// How soon the SDK retries after a failed refresh that handed back the
// current token.
export const REFRESH_RETRY_AFTER_MS = 60 * 1000;

interface TokenSource {
  // Mint a new subscriber token from scratch.
  fetchToken: () => Promise<SubscriberTokenResponse | null>;
  // Exchange a refresh token for a new subscriber token.
  refreshToken: (
    refreshToken: string,
  ) => Promise<SubscriberTokenResponse | null>;
  onRefreshFailed?: (error: unknown) => void;
}

/**
 * Supplies subscriber tokens to one long-lived SignalWire client.
 *
 * The provider used to hand the SDK a `StaticCredentialProvider`, which cannot
 * refresh, so renewing the token meant building a new client and closing the
 * old one — a fresh WebSocket every 50 minutes and on every tab wake. An answer
 * or dial sent while that swap was in flight went out on, or was answered
 * over, a socket that closed moments later: SignalWire traced the Pure Medical
 * Spa failures to exactly this. With `refresh()` implemented the SDK keeps the
 * same client and connection and renews the token itself, so nothing in the
 * console has to replace the client just to keep it authenticated.
 */
export class SubscriberCredentialProvider implements CredentialProvider {
  private token: string;
  private refreshTokenValue: string | null;
  private issuedAt: number;

  constructor(
    initial: SubscriberTokenResponse,
    private readonly source: TokenSource,
  ) {
    this.token = initial.token;
    this.refreshTokenValue = initial.refreshToken;
    this.issuedAt = Date.now();
  }

  async authenticate(): Promise<SDKCredential> {
    // The SDK also calls this when it reconnects. A token past its refresh
    // point (the refresh timer may not have run while the machine slept) is
    // reported as expired and rejected, so renew it first.
    if (Date.now() - this.issuedAt >= TOKEN_REFRESH_AFTER_MS) {
      return this.refresh();
    }
    return this.credential();
  }

  async refresh(): Promise<SDKCredential> {
    try {
      let next: SubscriberTokenResponse | null = null;
      const age = Date.now() - this.issuedAt;
      if (this.refreshTokenValue && age < REFRESH_TOKEN_MAX_AGE_MS) {
        next = await this.source.refreshToken(this.refreshTokenValue);
      }
      if (!next?.token) {
        next = await this.source.fetchToken();
      }
      if (!next?.token) {
        throw new Error("Subscriber token refresh returned no token");
      }
      this.token = next.token;
      this.refreshTokenValue = next.refreshToken;
      this.issuedAt = Date.now();
      return this.credential();
    } catch (error) {
      this.source.onRefreshFailed?.(error);
      // A throw counts as a strike in the SDK, and after five (about 20s) it
      // disconnects the client, hanging up any live call. The token we hold is
      // still good well past our 50-minute refresh point, so while it is, hand
      // it back with a short expiry: the SDK retries in a minute instead.
      if (Date.now() - this.issuedAt < TOKEN_USABLE_FOR_MS) {
        return {
          token: this.token,
          expiry_at: Date.now() + REFRESH_RETRY_AFTER_MS,
        };
      }
      throw error;
    }
  }

  private credential(): SDKCredential {
    return {
      token: this.token,
      expiry_at: this.issuedAt + TOKEN_REFRESH_AFTER_MS,
    };
  }
}
